import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { get } from '../nucleo/api';
import { data } from '../nucleo/formato';
import { useSessao } from '../nucleo/sessao';
import { Cartao } from '../componentes/base';
import { Carregando, Erro, Vazio } from '../componentes/estados';
import { SlotAcaoPrimaria } from '../componentes/acao-primaria';
import { TextoCortado } from '../componentes/texto-cortado';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { useDiscreto } from '../componentes/sensivel';
import { AcoesRapidas } from '../componentes/inicio/AcoesRapidas';
import { CartaoIndicador } from '../componentes/inicio/CartaoIndicador';
import { CartaoNovidades, novidadesVisiveis } from '../componentes/inicio/CartaoNovidades';
import { NOVIDADES } from '../novidades';
import { AjudaDaTela } from '../componentes/ajuda-tela';
import { CHAVE_TELA_INICIO } from '../nucleo/chave-da-tela';
import { agruparPorSecao, type IndicadorApi } from '../componentes/inicio/tipos';
import { SeletorPeriodo, useRotuloPeriodo } from '../componentes/periodo/SeletorPeriodo';
import { buscaComPeriodo, chavePeriodo, escolhaDoPeriodo, hojeNoFuso, lerPeriodoGuardado, periodoDaEscolha, resolverPeriodo, type PeriodoGuardado } from '../componentes/periodo/periodo';

function lerGuardado(usuarioId: string) {
  try {
    return lerPeriodoGuardado(localStorage.getItem(chavePeriodo(usuarioId)));
  } catch {
    return null;
  }
}

/**
 * F10 + fase 2 (W1, A5 5.10): Início em seções (Financeiro, Comercial, Operação, Pessoas e Sistema), com o seletor de
 * período. A escolha fica na URL (`?de=&ate=`, link compartilhável) e no aparelho, por usuário; datas no fuso do
 * workspace. Só os cards de fluxo obedecem ao período; posição mostra "hoje" e fila mostra "agora". Uma seção só
 * aparece quando o usuário tem algum card dela.
 */
export function Inicio() {
  const { t } = useTranslation();
  const s = useSessao();
  // Modo discreto: os valores remontam quando ele liga ou desliga.
  useDiscreto();
  const [busca, setBusca] = useSearchParams();
  const hoje = hojeNoFuso(s.workspace.fuso);
  const [guardado, setGuardado] = useState<PeriodoGuardado | null>(() => lerGuardado(s.usuario.id));
  const periodo = useMemo(() => resolverPeriodo({ de: busca.get('de'), ate: busca.get('ate') }, guardado, hoje), [busca, guardado, hoje]);
  const escolha = escolhaDoPeriodo(periodo, hoje);
  const rotular = useRotuloPeriodo();
  const rotuloEscolha = rotular(escolha, periodo);
  const intervalo = t('comum.intervalo', { de: data(periodo.de), ate: data(periodo.ate) });
  // O mês por extenso e os atalhos levam as datas ao lado; o intervalo livre já é as próprias datas.
  const rotuloPeriodo = rotuloEscolha === intervalo ? intervalo : `${rotuloEscolha} · ${intervalo}`;

  const aoEscolher = useCallback(
    (e: PeriodoGuardado) => {
      const p = periodoDaEscolha(e, hoje);
      try {
        localStorage.setItem(chavePeriodo(s.usuario.id), JSON.stringify(e));
      } catch {
        /* sem storage: vale a URL */
      }
      setGuardado(e);
      setBusca((atual) => buscaComPeriodo(atual, p), { replace: true });
    },
    [hoje, s.usuario.id, setBusca],
  );

  const q = useQuery({
    queryKey: ['inicio', periodo.de, periodo.ate],
    queryFn: () => get<IndicadorApi[]>('/inicio/indicadores', { de: periodo.de, ate: periodo.ate }),
    placeholderData: keepPreviousData,
  });
  // Fase 2 (W21, K7): a seção Sistema sempre existe quando há indicadores, para o card "Novidades" (do web, no fim dela).
  const temNovidades = novidadesVisiveis(NOVIDADES, { instalado: s.instalado, pode: s.pode }).length > 0;
  const secoes = useMemo(() => {
    const g = q.data ? agruparPorSecao(q.data) : [];
    return temNovidades && g.length && !g.some((x) => x.secao === 'sistema') ? [...g, { secao: 'sistema' as const, itens: [] }] : g;
  }, [q.data, temNovidades]);
  const tentar = useCallback(() => void q.refetch(), [q]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('inicio.kicker')}</p>
          <TextoCortado como="h1" className="mt-1 text-2xl font-bold tracking-tight">{t('inicio.titulo', { nome: s.usuario.nome.split(' ')[0] })}</TextoCortado>
          <SubtituloPagina caminho="/" />
          <p className="mt-1 text-sm text-tinta-3">{rotuloPeriodo}</p>
        </div>
        <div className="flex max-w-full min-w-0 flex-wrap items-center gap-2">
          {/* U15 F1: botão Ajuda do Início, antes do seletor de período. */}
          <AjudaDaTela chave={CHAVE_TELA_INICIO} caminho={t('menus.inicio')} />
          <SeletorPeriodo periodo={periodo} escolha={escolha} aoEscolher={aoEscolher} alvoTour="filtro" />
          <SlotAcaoPrimaria />
        </div>
      </header>
      <div aria-busy={q.isFetching} className="flex flex-col gap-8">
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={tentar} />
        ) : secoes.length === 0 ? (
          <Cartao>
            <Vazio texto={t('inicio.semIndicadores')} />
          </Cartao>
        ) : (
          secoes.map((g) => (
            <section key={g.secao} aria-labelledby={`secao-${g.secao}`} data-secao={g.secao} className="flex flex-col gap-3">
              <h2 id={`secao-${g.secao}`} className="text-sm font-semibold text-tinta-1">
                {t(`inicio.secoes.${g.secao}`)}
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {g.itens.map((i) => (
                  <CartaoIndicador key={i.chave} i={i} moeda={s.workspace.moeda} rotuloPeriodo={t('comum.intervalo', { de: data(periodo.de), ate: data(periodo.ate) })} aoTentar={tentar} />
                ))}
                {g.secao === 'sistema' && <CartaoNovidades />}
              </div>
            </section>
          ))
        )}
        <AcoesRapidas />
      </div>
    </div>
  );
}
